import { watch } from 'vue';
import { useForm } from 'vee-validate';
import { toValue, type MaybeRefOrGetter } from '@vueuse/core';
import { useBagContext } from '~/modules/add-to-bag/composables/useBagContext';

import { GIFT_CARD_TYPE } from '../constants/giftcard';
import { VALIDATION_SCHEMAS, INITIAL_VALUES } from '../constants/validationSchemas';
import { useGift } from './useGift';
import { useGiftPreselect } from './useGiftPreselect/useGiftPreselect';

/**
 * * Gift Card Form Composable
 *
 * @description
 * This composable is used to manage the gift card form.
 */
export const useGiftCardForm = (giftCardType: MaybeRefOrGetter<GIFT_CARD_TYPE> = GIFT_CARD_TYPE.BASIC) => {
  const { selectedGift, giftOptions } = useGift(toValue(giftCardType));
  const { isUpdateBagEnabled } = useBagContext();

  const form = useForm({
    initialValues: INITIAL_VALUES[toValue(giftCardType)],
    validationSchema: VALIDATION_SCHEMAS[toValue(giftCardType)],
  });

  const setSelectedGiftCard = (newValue?: string | null) => {
    if (newValue === null || newValue === undefined) {
      selectedGift.value = null;
      return;
    }
    const parsedValue = +newValue;
    const value = Number.isNaN(parsedValue) ? null : parsedValue;
    selectedGift.value = value;
  };

  watch(() => form.values.giftCardAmount, setSelectedGiftCard);
  watch(
    () => selectedGift.value,
    (newValue) => {
      const parsedValue = newValue === null ? '' : newValue.toString();
      if (form.values.giftCardAmount === parsedValue) {
        return;
      }
      form.values.giftCardAmount = parsedValue;
    },
  );

  if (isUpdateBagEnabled) {
    useGiftPreselect(form.values, selectedGift, giftCardType);
  }

  return {
    ...form,
    giftOptions,
    setSelectedGiftCard,
  };
};
